import { useEffect, useState } from 'react';
import { Share, Plus, X } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications.js';
import { Button } from '../ui/Button.js';
import { isIOS } from '../lib/native.js';

const DISMISS_KEY = 'nota_install_prompt_dismissed_at';
const RESURFACE_MS = 7 * 24 * 60 * 60 * 1000;

export function InstallPromptCard({
  dismissible = true,
}: {
  dismissible?: boolean;
}): React.ReactElement | null {
  const { isSupported, isStandalone } = useNotifications();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissible) return;
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return;
    const at = Number(raw);
    if (Number.isFinite(at) && Date.now() - at < RESURFACE_MS) {
      setDismissed(true);
    }
  }, [dismissible]);

  // Les étapes décrivent Safari sur iPhone : ailleurs (Android, bureau), la carte n'a rien à dire.
  if (!isIOS() || !isSupported || isStandalone || dismissed) return null;

  function onDismiss(): void {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }

  // Une carte simple (Braise 2) : le filet rouge à gauche la faisait passer pour une alerte, et
  // c'est un conseil qu'on peut fermer.
  return (
    <div className="carte carte-serree relative">
      {dismissible && (
        <Button
          variant="quiet"
          size="icon-small"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2"
        >
          <X size={16} aria-hidden />
        </Button>
      )}
      <h3 className="carte-titre pr-8">Install Nota on your iPhone</h3>
      <p className="discret mb-3">To receive notifications, add Nota to your home screen.</p>
      <ol className="discret pl-5 list-decimal space-y-1.5">
        <li>
          <span className="inline-flex items-center gap-2 align-middle">
            Tap <Share size={16} className="text-info" aria-label="Share" /> in Safari
          </span>
        </li>
        <li>
          <span className="inline-flex items-center gap-2 align-middle">
            Choose
            <span className="etiquette">
              <Plus size={12} aria-hidden /> Add to Home Screen
            </span>
          </span>
        </li>
        <li>Open Nota from the home screen</li>
      </ol>
    </div>
  );
}
